/* eslint-disable react/no-this-in-sfc */
import { green } from '@ant-design/colors';
import {
  DeleteOutlined,
  EditOutlined,
  UploadOutlined
} from '@ant-design/icons';
import { Button, Card, Modal, Upload } from 'antd';
import useBreakpoint from 'antd/lib/grid/hooks/useBreakpoint';
import React, { useEffect, useRef, useState } from 'react';
import SignatureCanvas from 'react-signature-canvas';
import Image360 from './Image360';

const Firma = ({
  text = 'Firmar',
  value,
  onChange,
  preview,
  disabled,
  error,
  onSuccess
}) => {
  const signatureRef = useRef({});
  const [imagePreview, setImagePreview] = useState('');
  const [visible, setVisible] = useState(false);
  const [uploadMode, setUploadMode] = useState();
  const [uploadedFile, setUploadedFile] = useState();
  const screens = useBreakpoint();
  const subirDisponible = false;

  const [width, setWidth] = useState(window.innerWidth * 0.95);
  const [height, setHeight] = useState(window.innerHeight * 0.6);
  const [orientation, setOrientation] = useState(window.orientation);

  function handleWindowSizeChange() {
    setWidth(window.innerWidth * 0.95);
    setHeight(window.innerHeight * 0.6);
    setOrientation(window.orientation);
  }

  useEffect(() => {
    window.addEventListener('resize', handleWindowSizeChange);
    return () => {
      window.removeEventListener('resize', handleWindowSizeChange);
    };
  }, []);

  // Takes a data URI and returns the Data URI corresponding to the resized image at the wanted size.
  function resizedataURL(datas, wantedWidth, wantedHeight) {
    // eslint-disable-next-line no-async-promise-executor
    return new Promise(async function test(resolve) {
      // We create an image to receive the Data URI
      const img = document.createElement('img');

      // When the event "onload" is triggered we can resize the image.
      img.onload = function t() {
        // We create a canvas and get its context.
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');

        // We set the dimensions at the wanted size.
        canvas.width = wantedWidth;
        canvas.height = wantedHeight;

        const w = this.naturalWidth;
        const h = this.naturalHeight;

        // Get the min scale to fit the image to the canvas
        const scale = Math.min(canvas.width / w, canvas.height / h);

        // Set the transform to scale the image, and center to the canvas
        ctx.setTransform(
          scale,
          0,
          0,
          scale,
          canvas.width / 2,
          canvas.height / 2
        );

        // draw the image offset by half its width and height to center and fit
        ctx.drawImage(this, -w / 2, -h / 2, w, h);
        const dataURI = canvas.toDataURL();
        // This is the return of the Promise
        resolve(dataURI);
      };

      // We put the Data URI in the image's src attribute
      img.src = datas;
    });
  } // Use it like : var newDataURI = await resizedataURL('yourDataURIHere', 50, 50);

  const showModal = () => {
    setVisible(true);
  };

  const handleOk = async () => {
    const data = signatureRef.current.getTrimmedCanvas().toDataURL('image/jpg');
    let resized;
    if (signatureRef.current.isEmpty()) {
      resized = 'data:image/png;base64,';
    } else {
      resized = await resizedataURL(data, 400, 400);
    }
    setVisible(false);
    if (onSuccess) {
      if (resized) {
        onSuccess(resized);
      } else {
        onSuccess(uploadedFile);
      }
    }
    if (resized) {
      onChange?.(resized);
    } else {
      onChange?.(uploadedFile);
    }
  };

  const handleCancel = () => {
    setVisible(false);
  };

  const handleClear = () => {
    if (signatureRef.current) {
      signatureRef.current.clear();
    } else {
      setUploadMode(false);
      setUploadedFile(undefined);
    }
  };

  function getBase64(img, callback) {
    const reader = new FileReader();
    reader.addEventListener('load', () => callback(reader.result));
    reader.readAsDataURL(img);
  }

  const props = {
    name: 'file',
    showUploadList: false,
    beforeUpload(info) {
      getBase64(info, (imageUrl) => {
        setUploadedFile(imageUrl);
        setUploadMode(true);
      });
      return false;
    }
  };

  useEffect(() => {
    if (value) {
      setImagePreview(`data:image/png;base64,${value || ''}`);
    } else {
      setImagePreview();
    }
  }, [value]);

  return (
    <div>
      {preview && imagePreview ? (
        <Card
          size="default"
          style={{ width: 400 }}
          bodyStyle={{ padding: 0 }}
          actions={[
            <Button
              type="link"
              disabled={disabled}
              icon={<EditOutlined />}
              onClick={showModal}
              style={{ color: 'inherit' }}
            >
              Editar
            </Button>
          ]}
        >
          <Image360
            src={imagePreview}
            alt="avatar"
            style={{
              objectFit: 'contain',
              padding: 4
            }}
          />
        </Card>
      ) : (
        <Button
          disabled={disabled}
          danger={error}
          style={{
            color: !error && green.primary,
            borderColor: !error && green.primary
          }}
          icon={<EditOutlined />}
          onClick={showModal}
        >
          {text}
        </Button>
      )}

      <Modal
        bodyStyle={{ padding: 0 }}
        centered
        width={width}
        title="Firme aquí"
        open={visible}
        onOk={handleOk}
        onCancel={handleCancel}
        okButtonProps={{ disabled: true }}
        cancelButtonProps={{ disabled: true }}
        footer={[
          subirDisponible && (
            <Upload {...props}>
              <Button key="upload" type="round" style={{ marginRight: 8 }}>
                <UploadOutlined />
                {screens.md === true && 'Subir'}
              </Button>
            </Upload>
          ),
          <Button key="clear" type="dashed" danger onClick={handleClear}>
            <DeleteOutlined />
            {screens.md === true && 'Borrar'}
          </Button>,
          <Button key="back" onClick={handleCancel}>
            Cancelar
          </Button>,
          <Button key="submit" type="primary" onClick={handleOk}>
            Aceptar
          </Button>
        ]}
      >
        <div style={{ width: '100%', height: '100%' }}>
          <div
            style={{
              width: '100%',
              height: '100%',
              margin: '0 auto',
              textAlign: 'center'
            }}
          >
            {uploadMode && (
              <img
                style={{ maxWidth: 300, width: '100%' }}
                src={uploadedFile}
                alt="uploaded-file"
              />
            )}
            {!uploadMode && (
              <SignatureCanvas
                canvasProps={{
                  height: orientation === 0 ? 400 : height,
                  width
                }}
                ref={signatureRef}
                onBegin={() => {
                  setUploadMode(false);
                  setUploadedFile(undefined);
                }}
              />
            )}
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default Firma;
